import React, { useState } from "react";
import {
  HiShieldExclamation,
  HiClock,
  HiCheckCircle,
  HiPlus,
  HiTrash,
  HiRefresh,
  HiX,
} from "react-icons/hi";

const BlockedIPs = () => {
  const [blockedIPs, setBlockedIPs] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newIP, setNewIP] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [newDuration, setNewDuration] = useState("24");
  const [newReason, setNewReason] = useState("");

  const stats = {
    totalBlocked: blockedIPs.length,
    activeBlocks: blockedIPs.filter(
      (ip) => new Date(ip.expiresAt) > new Date()
    ).length,
    expiredBlocks: blockedIPs.filter(
      (ip) => new Date(ip.expiresAt) <= new Date()
    ).length,
  };

  const handleAddIP = () => {
    if (!newIP.trim()) return;

    const newBlock = {
      id: Date.now(),
      ipAddress: newIP.trim(),
      email: newEmail.trim() || "N/A",
      blockedAt: new Date().toISOString(),
      expiresAt: new Date(
        Date.now() + Number(newDuration) * 3600000
      ).toISOString(),
      violations: 0,
      requestCount: 0,
      reason: newReason || "Manual block",
    };

    setBlockedIPs((prev) => [...prev, newBlock]);
    setNewIP("");
    setNewEmail("");
    setNewDuration("24");
    setNewReason("");
    setShowAddModal(false);
  };

  const handleRemoveIP = (id) => {
    setBlockedIPs((prev) => prev.filter((ip) => ip.id !== id));
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleString();
  };

  const getTimeRemaining = (expiresAt) => {
    const diff = new Date(expiresAt) - new Date();
    if (diff <= 0) return "Expired";
    const hours = Math.floor(diff / 3600000);
    const mins = Math.floor((diff % 3600000) / 60000);
    if (hours > 24) return `${Math.floor(hours / 24)}d ${hours % 24}h`;
    return `${hours}h ${mins}m`;
  };

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Blocked IPs</h1>
          <p className="text-gray-400 text-sm mt-1">
            Manage blocked IP addresses and their restrictions
          </p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="flex items-center gap-2 bg-[#BDE958] hover:bg-[#a8d44a] text-[#161616] px-4 py-2.5 rounded-lg text-sm font-bold transition-colors"
        >
          <HiPlus className="w-4 h-4" />
          Block new IP
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="bg-[#1E1E1E] rounded-xl border border-white/5 p-5 flex items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-red-500/10 flex items-center justify-center">
            <HiShieldExclamation className="w-6 h-6 text-red-400" />
          </div>
          <div>
            <p className="text-xs text-gray-400 font-medium uppercase tracking-wide">
              Total Blocked
            </p>
            <p className="text-3xl font-black text-white mt-1">
              {stats.totalBlocked}
            </p>
          </div>
        </div>
        <div className="bg-[#1E1E1E] rounded-xl border border-white/5 p-5 flex items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-orange-500/10 flex items-center justify-center">
            <HiClock className="w-6 h-6 text-orange-400" />
          </div>
          <div>
            <p className="text-xs text-gray-400 font-medium uppercase tracking-wide">
              Active Blocks
            </p>
            <p className="text-3xl font-black text-white mt-1">
              {stats.activeBlocks}
            </p>
          </div>
        </div>
        <div className="bg-[#1E1E1E] rounded-xl border border-white/5 p-5 flex items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-green-500/10 flex items-center justify-center">
            <HiCheckCircle className="w-6 h-6 text-green-400" />
          </div>
          <div>
            <p className="text-xs text-gray-400 font-medium uppercase tracking-wide">
              Expired Blocks
            </p>
            <p className="text-3xl font-black text-white mt-1">
              {stats.expiredBlocks}
            </p>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-[#1E1E1E] rounded-xl border border-white/5 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/5">
                <th className="text-left px-5 py-3.5 text-gray-400 font-semibold text-xs uppercase tracking-wide">
                  IP Address
                </th>
                <th className="text-left px-5 py-3.5 text-gray-400 font-semibold text-xs uppercase tracking-wide">
                  Email
                </th>
                <th className="text-left px-5 py-3.5 text-gray-400 font-semibold text-xs uppercase tracking-wide">
                  Blocked At
                </th>
                <th className="text-left px-5 py-3.5 text-gray-400 font-semibold text-xs uppercase tracking-wide">
                  Expires In
                </th>
                <th className="text-left px-5 py-3.5 text-gray-400 font-semibold text-xs uppercase tracking-wide">
                  Violations
                </th>
                <th className="text-left px-5 py-3.5 text-gray-400 font-semibold text-xs uppercase tracking-wide">
                  Request Count
                </th>
                <th className="text-left px-5 py-3.5 text-gray-400 font-semibold text-xs uppercase tracking-wide">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {blockedIPs.length === 0 ? (
                <tr>
                  <td
                    colSpan="7"
                    className="text-center py-16 text-gray-500 text-sm"
                  >
                    No blocked IP addresses found
                  </td>
                </tr>
              ) : (
                blockedIPs.map((ip) => (
                  <tr
                    key={ip.id}
                    className="border-b border-white/5 hover:bg-white/[0.02] transition-colors"
                  >
                    <td className="px-5 py-3.5 text-white font-mono text-sm">
                      {ip.ipAddress}
                    </td>
                    <td className="px-5 py-3.5 text-gray-300">{ip.email}</td>
                    <td className="px-5 py-3.5 text-gray-400">
                      {formatDate(ip.blockedAt)}
                    </td>
                    <td className="px-5 py-3.5">
                      <span
                        className={`text-sm font-medium ${
                          getTimeRemaining(ip.expiresAt) === "Expired"
                            ? "text-red-400"
                            : "text-green-400"
                        }`}
                      >
                        {getTimeRemaining(ip.expiresAt)}
                      </span>
                    </td>
                    <td className="px-5 py-3.5 text-gray-300">
                      {ip.violations}
                    </td>
                    <td className="px-5 py-3.5 text-gray-300">
                      {ip.requestCount}
                    </td>
                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleRemoveIP(ip.id)}
                          className="p-1.5 rounded-lg text-red-400 hover:bg-red-500/10 transition-colors"
                          title="Remove block"
                        >
                          <HiTrash className="w-4 h-4" />
                        </button>
                        <button
                          className="p-1.5 rounded-lg text-gray-400 hover:bg-white/5 transition-colors"
                          title="Refresh"
                        >
                          <HiRefresh className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="bg-[#1E1E1E] rounded-2xl border border-white/10 w-full max-w-md mx-4 p-6">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-lg font-bold text-white">Block New IP</h2>
              <button
                onClick={() => setShowAddModal(false)}
                className="p-1 rounded-lg text-gray-400 hover:bg-white/5"
              >
                <HiX className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1.5">
                  IP Address *
                </label>
                <input
                  type="text"
                  value={newIP}
                  onChange={(e) => setNewIP(e.target.value)}
                  placeholder="e.g., 192.168.1.100"
                  className="w-full bg-[#161616] border border-white/10 rounded-lg px-3.5 py-2.5 text-white text-sm placeholder-gray-500 focus:outline-none focus:border-[#BDE958]/50"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1.5">
                  Associated Email
                </label>
                <input
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  placeholder="e.g., user@example.com"
                  className="w-full bg-[#161616] border border-white/10 rounded-lg px-3.5 py-2.5 text-white text-sm placeholder-gray-500 focus:outline-none focus:border-[#BDE958]/50"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1.5">
                  Block Duration (hours)
                </label>
                <select
                  value={newDuration}
                  onChange={(e) => setNewDuration(e.target.value)}
                  className="w-full bg-[#161616] border border-white/10 rounded-lg px-3.5 py-2.5 text-white text-sm focus:outline-none focus:border-[#BDE958]/50"
                >
                  <option value="1">1 hour</option>
                  <option value="6">6 hours</option>
                  <option value="12">12 hours</option>
                  <option value="24">24 hours</option>
                  <option value="72">3 days</option>
                  <option value="168">7 days</option>
                  <option value="720">30 days</option>
                  <option value="8760">Permanent (1 year)</option>
                </select>
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1.5">
                  Reason
                </label>
                <input
                  type="text"
                  value={newReason}
                  onChange={(e) => setNewReason(e.target.value)}
                  placeholder="e.g., Rate limit abuse"
                  className="w-full bg-[#161616] border border-white/10 rounded-lg px-3.5 py-2.5 text-white text-sm placeholder-gray-500 focus:outline-none focus:border-[#BDE958]/50"
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowAddModal(false)}
                className="flex-1 px-4 py-2.5 rounded-lg text-sm font-medium text-gray-300 border border-white/10 hover:bg-white/5 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleAddIP}
                disabled={!newIP.trim()}
                className="flex-1 px-4 py-2.5 rounded-lg text-sm font-bold bg-[#BDE958] text-[#161616] hover:bg-[#a8d44a] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                Block IP
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BlockedIPs;
